import { useEffect, useMemo, useRef, useState } from 'react';
import { useAvisosUsuario, ouvirAvisoConfirmadoEmOutraAba } from '@/hooks/useAvisosUsuario';
import { useAuth } from '@/hooks/useAuth';
import { AvisoPopup } from './AvisoPopup';
import { AvisoCampanhaFullscreen } from './AvisoCampanhaFullscreen';
import { AvisosConvergenciaAgrupado } from './AvisosConvergenciaAgrupado';
import type { AvisoConvergencia } from './AvisosConvergenciaAgrupado';
import { AvisosChamadoCarteiraAgrupado } from './AvisosChamadoCarteiraAgrupado';
import { AvisosCanceladoAgrupado } from './AvisosCanceladoAgrupado';
import type { AvisoCancelado } from './AvisosCanceladoAgrupado';
import type { AvisoChamadoCarteira } from './AvisosChamadoCarteiraAgrupado';
import AvisoAjudaRespondida, { type AvisoRespondida } from '@/components/tarefas/AvisoAjudaRespondida';
import { tipoCartaoTarefa } from '@/lib/ajudaRespondida';
import AvisoContratoUrgente, { ORIGENS_CONTRATO, type AvisoContrato } from '@/components/tarefas/AvisoContratoUrgente';

/* ⛔ (Douglas 09/09/2026) ROBÔ NÃO SEQUESTRA TELA. AVISO É FERRAMENTA DE GENTE.
   "eu nao mandei colocar nada disso nos avisos em tela." E depois, sobre os dois
   que tinham sobrado: "tire este erro de input em tela... e cliente voltou a
   falar tbm tire."
   Não havia porteiro: QUALQUER `INSERT INTO avisos` com tipo urgente, prioridade
   crítica ou requer_confirmacao abria modal sozinho — com beep, sem X, e o botão
   abre outra aba. Cada frente nova (falha da IA, tarefa, Bora Fazer,
   recuperação de venda, transferência humana, erro de input) achou essa porta e
   usou. Chegou a 125 modais/dia: Brenda 18,4, William 16,7, Eduarda 10.
   A régua agora:
     · item cancelado  -> TELA AGRUPADA (continua avisando, não trava — o
       consultor e o supervisor têm que ver os pedidos que caíram);
     · recado escrito à mão no Painel de Avisos -> modal, como sempre foi. É para
       isso que a ferramenta existe;
     · qualquer aviso de FLUXO (tem marcador em metadados) -> não abre modal.
   Medido em 90 dias: 6.098 avisos de fluxo contra ZERO recado escrito à mão —
   ou seja, o robô tinha tomado a ferramenta inteira do Douglas. */
const MARCADORES_DE_FLUXO = ['origem', 'tipo_aviso', 'tipo', 'acao_navegacao'];

const ehItemCancelado = (aviso: { metadados?: Record<string, unknown> | null }): boolean =>
  (aviso?.metadados ?? {}).tipo_aviso === 'cancelamento_item';

/* Recado escrito à mão = nenhum marcador de fluxo nos metadados. Fluxo novo que
   precise da tela passa pelo Douglas — e entra AQUI, nunca no INSERT. */
const ehRecadoDeGente = (aviso: { metadados?: Record<string, unknown> | null }): boolean => {
  const m = aviso?.metadados ?? {};
  return !MARCADORES_DE_FLUXO.some((chave) => m[chave] != null);
};

export default function AvisosUsuario() {
  const { avisos, loading, registrarVisualizacao, confirmarAviso } = useAvisosUsuario();
  const { user } = useAuth();
  const [avisoAtual, setAvisoAtual] = useState<any>(null);
  // Estado em memória apenas — fechamento é "por sessão". Próxima abertura do CRM
  // re-renderiza este componente e o destaque volta automaticamente enquanto ativo.
  const [destaqueDispensado, setDestaqueDispensado] = useState(false);
  const autoAbertos = useRef<Set<string>>(new Set());

  // (Douglas 26/07) TRANSFERÊNCIA SAI DA FILA DE MODAIS. Só ela — os outros
  // fluxos (🚨 Item Cancelado, ⚠️ Supervisora) seguem exatamente como estavam.
  // Transferência vira UMA tela com o total por tipo; a ação continua caso a
  // caso lá dentro, sem confirmar-tudo.
  // (Douglas 17/09/2026) A TRANSFERÊNCIA SAIU. O aviso era uma camada em cima de
  // algo que já estava lá: medido nos 79 casos abertos, 100% tinham a conversa
  // no Inbox e 100% tinham dono — e só 3 clientes estavam de fato esperando.
  // Com a LEI DA VISÃO, quem precisa ver a conversa já vê. O aviso só somava
  // popup. Em 03/10/2026 a transferência saiu do CRM inteiro.
  const { destaques, convergencias, chamados, cancelados, respondidas, contratos, normais } = useMemo(() => {
    const dest: any[] = [];
    const contr: AvisoContrato[] = [];
    const resp: AvisoRespondida[] = [];
    const conv: typeof avisos = [];
    const cham: typeof avisos = [];
    const canc: typeof avisos = [];
    const norm: any[] = [];
    for (const a of avisos) {
      if ((a as any).modo_destaque) dest.push(a);
      else if (ehItemCancelado(a)) canc.push(a);
      else if ((ORIGENS_CONTRATO as readonly string[]).includes(String(a.metadados?.origem))) contr.push(a as unknown as AvisoContrato);
      else if (a.metadados?.origem === 'convergencia_reanalise') conv.push(a);
      else if (a.metadados?.origem === 'chamado_carteira') cham.push(a);
      else if (tipoCartaoTarefa(a as AvisoRespondida)) resp.push(a as AvisoRespondida);
      else norm.push(a);
    }
    return { destaques: dest, convergencias: conv, chamados: cham, cancelados: canc, respondidas: resp, contratos: contr, normais: norm };
  }, [avisos]);

  useEffect(() => ouvirAvisoConfirmadoEmOutraAba((avisoId) => {
    setAvisoAtual((atual: any) => (atual?.id === avisoId ? null : atual));
  }), []);

  // Abre sozinho (1x por sessão por aviso) SÓ recado escrito à mão que seja
  // urgente / crítico / com confirmação. Aviso de fluxo nunca sequestra a tela.
  useEffect(() => {
    if (loading || avisoAtual) return;
    // Exceção aprovada pelo Douglas (26/09/2026): o aviso diário "Ativação do
    // dia" é fluxo, mas abre sozinho — é para a pessoa ver ao entrar no CRM.
    // Exceção de 02/10/2026: a TAREFA VENCENDO abre no aviso central (Tratar
    // agora / Tratar depois). O cartão no canto podia passar sem ser visto.
    // A chave leva o data_inicio: o Tratar depois devolve o MESMO aviso 30 min
    // depois, e ele tem que abrir de novo na mesma sessão.
    const chave = (a: { id: string; data_inicio: string }) => `${a.id}|${a.data_inicio}`;
    // Exceção de 06/10/2026: o PEDIDO NOVO DA CONECTA abre na tela da supervisora CPF
    // e do Douglas, igual ao cartão do celular ("ela não viu que gerou um pedido").
    const urgente = normais.find((a: any) =>
      (ehRecadoDeGente(a) || a.tipo === 'ativacao_dia' || a.metadados?.origem === 'tarefa' || a.metadados?.origem === 'pedido_conecta') &&
      (a.tipo === 'urgente' || a.prioridade === 'critica' || a.requer_confirmacao || a.exibir_sempre) &&
      !autoAbertos.current.has(chave(a))
    );
    if (urgente) {
      autoAbertos.current.add(chave(urgente));
      setAvisoAtual(urgente);
    }
  }, [normais, loading, avisoAtual]);

  if (loading) {
    return null;
  }

  // Modal fullscreen tem prioridade absoluta sobre qualquer popup secundário
  const mostrarDestaque = !destaqueDispensado && destaques.length > 0;
  const mostrarConvergencias = !mostrarDestaque && convergencias.length > 0;
  // Chamado de carteira é cliente esperando, e quem pediu foi gente do time.
  const mostrarChamados = !mostrarDestaque && !mostrarConvergencias && chamados.length > 0;
  // Pedido cancelado entra depois de cliente esperando: é dinheiro que caiu, não
  // gente na linha. Mas continua na tela — é o único fluxo que o Douglas manteve.
  const mostrarCancelados = !mostrarDestaque && !mostrarConvergencias
    && !mostrarChamados && cancelados.length > 0;

  return (
    <>
      {mostrarDestaque && (
        <AvisoCampanhaFullscreen
          avisos={destaques}
          onClose={() => setDestaqueDispensado(true)}
          onVisualizar={registrarVisualizacao}
        />
      )}

      {respondidas.length > 0 && <AvisoAjudaRespondida avisos={respondidas} onConfirmar={confirmarAviso} />}

      {contratos.length > 0 && <AvisoContratoUrgente avisos={contratos} onConfirmar={confirmarAviso} />}

      {mostrarConvergencias && <AvisosConvergenciaAgrupado avisos={convergencias as unknown as AvisoConvergencia[]} />}

      {mostrarChamados && <AvisosChamadoCarteiraAgrupado avisos={chamados as unknown as AvisoChamadoCarteira[]} />}

      {mostrarCancelados && (
        <AvisosCanceladoAgrupado
          avisos={cancelados as unknown as AvisoCancelado[]}
          onConfirmar={confirmarAviso}
        />
      )}

      {/* ⛔ SINO FLUTUANTE REMOVIDO (Douglas 01/09/2026: "não existe isso").
          Ele ficava fixo no canto, em cima do rodapé das tabelas e da seta de
          passar para o próximo lead, e contava avisos que o Mural nem lista
          (o mural só mostra comunicação humana) — badge "1" e tela vazia.
          Aviso que precisa de gente já chega como popup; recado espera no
          mural, sem FAB cobrindo dado. Não recolocar. */}

      {!mostrarDestaque && !mostrarConvergencias && !mostrarChamados
        && !mostrarCancelados && avisoAtual && (
        <AvisoPopup
          aviso={avisoAtual}
          onClose={() => setAvisoAtual(null)}
          onConfirmar={confirmarAviso}
          onVisualizar={registrarVisualizacao}
        />
      )}
    </>
  );
}
